import React, { useState } from "react";

const FlipCard = ({ icon, title, subtitle, children }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <button
      type="button"
      className={`gog-flip-card-container ${flipped ? "is-active" : ""}`}
      onClick={() => setFlipped(!flipped)}
      aria-label={`Read more about ${title}`}
    >
      <div className={`gog-flip-card ${flipped ? "flipped" : ""}`}>
        <div className="gog-flip-card-face gog-flip-card-front">
          <div className="gog-flip-icon">{icon}</div>

          <h2>{title}</h2>

          {subtitle && <p>{subtitle}</p>}

          <span className="gog-flip-hint">Tap to flip →</span>
        </div>

        <div className="gog-flip-card-face gog-flip-card-back">
          {children}

          <span className="gog-flip-back-hint">Tap again to return</span>
        </div>
      </div>
    </button>
  );
};

const LandingPage = () => {
  const createListingClickHandle = () => {
    const pathEnv = process.env.REACT_APP_IDBF_BASE_GOG_REG;

    if (pathEnv) {
      window.location.assign(pathEnv);
    }
  };

  const enterDirectoryClickHandle = () => {
    window.location.assign("/home");
  };

  return (
    <div className="gog-page gog-page-spaced gog-landing-page">
      {/* HERO */}
      <section className="gog-landing-hero">
        <div className="gog-landing-hero-content">
          <span className="gog-eyebrow">GiveOrGo Marketplace</span>

          <h1>
            Find merchants, gift cards, promos, and web stores in one simple
            place.
          </h1>

          <p>
            GiveOrGo helps customers quickly discover local businesses, visit
            their websites, buy digital gift cards, view promotions, and connect
            with merchants online.
          </p>

          <div className="gog-landing-hero-actions">
            <button
              type="button"
              className="gog-primary-btn"
              onClick={enterDirectoryClickHandle}
            >
              Enter Directory
            </button>

            <button
              type="button"
              className="gog-secondary-btn"
              onClick={createListingClickHandle}
            >
              Create Account
            </button>
          </div>
        </div>

        <div className="gog-landing-hero-card">
          <div className="gog-landing-stat">
            <span>01</span>
            <strong>Search</strong>
            <p>Find merchants by location, services, gift cards, and web store options.</p>
          </div>

          <div className="gog-landing-stat">
            <span>02</span>
            <strong>Discover</strong>
            <p>View business details, images, keywords, categories, and contact info.</p>
          </div>

          <div className="gog-landing-stat">
            <span>03</span>
            <strong>Visit or Buy</strong>
            <p>Go to their website, web store, social pages, or purchase a gift card.</p>
          </div>
        </div>
      </section>

      {/* INTRO CARD */}
      <section className="gog-landing-intro-card">
        <div>
          <span className="gog-section-pill">Simple local discovery</span>
          <h2>A cleaner way to connect customers with merchants.</h2>
        </div>

        <p>
          The giveorgo.com marketplace provides you with a quick and easy way to
          locate merchants that match your specific needs. Once selected, you
          can visit their web store, web site, or social platforms, in addition
          to purchasing digital gift cards or receiving promos from them.
        </p>
      </section>

      {/* FEATURE STRIP */}
      <section className="gog-landing-feature-strip">
        <div>
          <strong>Gift Cards</strong>
          <span>Buy and manage digital gift cards.</span>
        </div>

        <div>
          <strong>Promotions</strong>
          <span>Discover offers from listed merchants.</span>
        </div>

        <div>
          <strong>Web Stores</strong>
          <span>Jump directly to merchant stores.</span>
        </div>

        <div>
          <strong>Local Search</strong>
          <span>Filter by location and business attributes.</span>
        </div>
      </section>

      {/* FLIP CARDS */}
      <section className="gog-landing-card-grid">
        <FlipCard
          icon="🛡️"
          title="100% Gift Card Guarantee"
          subtitle="Protection and peace of mind for gift card purchases."
        >
          <div className="gog-flip-content">
            <p>
              The money on any gift card that you have purchased is fully
              protected against loss even if the merchant of the card is no
              longer in business.
            </p>

            <p>
              By logging in to your fully secure, password protected account you
              will be able to transfer the funds on your card to any merchant on
              the GiveOrGo.com web site in the event of a merchant failure.
              Also, if a gift card is inadvertently deleted or lost track of,
              you will be able to resend that gift card to the recipient of your
              choice. Should you no longer want to have the money on a gift card,
              you will be able to refund the balance to your credit card with
              only a small restocking fee. Please see the section below on
              creating an account to learn more about the benefits of having an
              IDBillfold account for your GiveOrGo.com purchases.
            </p>
          </div>
        </FlipCard>

        <FlipCard
          icon="💳"
          title="Creating an Account"
          subtitle="Track gift cards, balances, transactions, and payment tokens."
        >
          <div className="gog-flip-content">
            <p>
              This will allow you to track and manage your gift card purchases.
              In addition, other options are available to enhance your gift card
              experience at other merchants listed in the GiveOrGo directory.
            </p>

            <p>
              Having an account is a free option for GiveOrGo.com customers.
              This account will allow you to:
            </p>

            <ul>
              <li>View gift card transaction history</li>
              <li>Check your gift card balances</li>
              <li>Add funds to your gift card balances</li>
              <li>Maintain list of Send To email addresses</li>
            </ul>

            <p>
              In addition to the other gift card management features listed in
              the 100% Guarantee section, you will have the option to:
            </p>

            <ul>
              <li>
                Obtain a token to be used instead of your credit card number for
                gift card purchases
              </li>
              <li>
                Display a QR code on your phone to pay for purchases at other
                merchants
              </li>
            </ul>

            <p>
              To create an account, click on the link below. You can go to
              IDBillfold.com at any time to manage your account.
            </p>

            <div className="gog-flip-action-row">
              <button
                type="button"
                className="gog-primary-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  createListingClickHandle();
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        </FlipCard>

        <FlipCard
          icon="🏪"
          title="Retailers / Restauranters"
          subtitle="Create a listing and promote your business online."
        >
          <div className="gog-flip-content">
            <p>
              Get a free web page listing in the GiveOrGo.com directory to
              showcase your business and offer gift cards and promos to attract
              new customers to your physical or web locations.
            </p>

            <p>
              A listing in the GiveOrGo.com directory can be made without
              requiring the offering of gift card or promo services.
            </p>

            <p>
              Create a web page to promote your business by highlighting what
              products and services you offer.
            </p>

            <p>
              The listing comes with free browser-based redemption software.
            </p>

            <p>
              The amount of the gift card redemption will be automatically
              deposited.
            </p>

            <p>
              Click on the link below to set up your marketing page.
            </p>

            <div className="gog-flip-action-row">
              <button
                type="button"
                className="gog-primary-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  createListingClickHandle();
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        </FlipCard>
      </section>
    </div>
  );
};

export default LandingPage;